import React, { useEffect, useState } from "react";

const ALL_REC_OPTIONS = ["STRONG BUY","BUY","STRONG SELL","SELL"];
const PULSE_OPTIONS = ["bullish","bearish","neutral","caution","whipsaw","distribution","recovery"];
const MARKET_PULSE_RULE_OPTIONS = [
  "Bear 1",
  "Bear 2",
  "Bear 3",
  "Bear 3.5",
  "Bear 4",
  "Bull 1",
  "Bull 2",
  "Bull 3",
  "Bull 3.5",
  "Bull 4",
  "Caution 1",
  "Caution 2",
  "Caution 3",
  "Flip Bear 1",
  "Flip Bear 2",
  "Flip Bull 1",
  "Flip Bull 2",
  "Intraday Override (Exhaustion+ED - manual)",
  "Intraday Override (Streak-7)",
  "Intraday Override (Exhaustion+ED)",
  "Neutral 1",
  "Neutral 2",
  "No Rule",
  "Whipsaw 1"
];

export default function ConditionalRecRuleEditor({
  rules,
  setRules,
  label = "Conditional Recommendation Rules"
}) {
  const [collapsedMap, setCollapsedMap] = useState({});

  useEffect(() => {
    setCollapsedMap((prev) => {
      const next = {};
      (rules || []).forEach((_, idx) => {
        next[idx] = prev[idx] ?? false;
      });
      return next;
    });
  }, [rules]);

  const hasRules = (rules || []).length > 0;
  const areAllCollapsed =
    hasRules && (rules || []).every((_, idx) => Boolean(collapsedMap[idx]));

  const setAllCollapsed = (collapsed) => {
    const next = {};
    (rules || []).forEach((_, idx) => {
      next[idx] = collapsed;
    });
    setCollapsedMap(next);
  };

  const addRule = () =>
    setRules([...(rules || []), {
      minScore: undefined,
      maxScore: undefined,
      allowedRecommendations: [],
      pulses: [],
      marketPulseRules: [],
      tpOverridePct: undefined,
    }]);

  const removeRule = (idx) => setRules((rules || []).filter((_, i) => i !== idx));

  const move = (idx, dir) => {
    const arr = [...(rules || [])];
    const ni = idx + dir;
    if (ni < 0 || ni >= arr.length) return;
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    setRules(arr);
  };

  const update = (idx, patch) => {
    const arr = [...(rules || [])];
    arr[idx] = { ...arr[idx], ...patch };
    setRules(arr);
  };

  const toggleCollapsed = (idx) => {
    setCollapsedMap((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const getRecBadgeClass = (rec) => {
    const val = String(rec || "").toUpperCase();

    if (val === "STRONG BUY" || val === "BUY") return "summary-pill-rec-buy";
    if (val === "STRONG SELL" || val === "SELL") return "summary-pill-rec-sell";

    return "summary-pill-neutral";
  };

  const getPulseBadgeClass = (pulse) => {
    const val = String(pulse || "").toLowerCase();

    if (val === "bullish") return "summary-pill-pulse-bullish";
    if (val === "bearish") return "summary-pill-pulse-bearish";
    if (val === "caution") return "summary-pill-pulse-caution";
    if (val === "neutral") return "summary-pill-pulse-neutral";
    if (val === "recovery") return "summary-pill-pulse-recovery";
    if (val === "distribution") return "summary-pill-pulse-distribution";
    if (val === "whipsaw") return "summary-pill-pulse-whipsaw";

    return "summary-pill-neutral";
  };

  const toggleRec = (idx, rec) => {
    const arr = [...(rules || [])];
    const set = new Set(arr[idx].allowedRecommendations || []);
    const val = rec.toUpperCase();
    if (set.has(val)) set.delete(val); else set.add(val);
    arr[idx].allowedRecommendations = Array.from(set);
    setRules(arr);
  };

  const togglePulse = (idx, pulse) => {
    const arr = [...(rules || [])];
    const set = new Set((arr[idx].pulses || []).map(p => p.toLowerCase()));
    const val = pulse.toLowerCase();
    if (set.has(val)) set.delete(val); else set.add(val);
    arr[idx].pulses = Array.from(set);
    setRules(arr);
  };

  const toggleMarketPulseRule = (idx, rule) => {
    const arr = [...(rules || [])];
    const current = arr[idx].marketPulseRules || [];
    const exists = current.some(x => String(x).toLowerCase() === rule.toLowerCase());

    arr[idx].marketPulseRules = exists
      ? current.filter(x => String(x).toLowerCase() !== rule.toLowerCase())
      : [...current, rule];

    setRules(arr);
  };

  const fmtRange = (minScore, maxScore) => {
    const hasMin = Number.isFinite(minScore);
    const hasMax = Number.isFinite(maxScore);

    if (hasMin && hasMax) return `Score: ${minScore} → ${maxScore}`;
    if (hasMin) return `Score: ≥ ${minScore}`;
    if (hasMax) return `Score: ≤ ${maxScore}`;
    return "Score: any";
  };

  const fmtList = (label, arr, fallback = "any") => {
    if (!arr || arr.length === 0) return `${label}: ${fallback}`;
    return `${label}: ${arr.join(", ")}`;
  };

  const getRuleSummary = (r) => {
    const parts = [
      fmtRange(r.minScore, r.maxScore),
      fmtList("Rec", r.allowedRecommendations, "all"),
      fmtList("Pulse", r.pulses, "any"),
      fmtList("Rule", r.marketPulseRules, "any"),
      r.tpOverridePct != null ? `TP: ${r.tpOverridePct}%` : "TP: none",
    ];
    return parts.join(" | ");
  };

  return (
    <div className="fb-section">
      <div className="fb-section-head">
        <div className="fb-section-title">{label}</div>
        <div className="fb-actions">
          <button type="button" className="link" onClick={addRule}>Add rule</button>

          <button
            type="button"
            className="link"
            onClick={() => setAllCollapsed(!areAllCollapsed)}
            disabled={!hasRules}
          >
            {areAllCollapsed ? "Expand all" : "Collapse all"}
          </button>

          <button type="button" className="link" onClick={() => setRules([])}>Clear all</button>
        </div>
      </div>

      {(rules || []).length === 0 && (
        <div className="hint">
          No conditional rules. Add rules to constrain recommendations by score range (and optional Market Pulse).
        </div>
      )}

      {(rules || []).map((r, idx) => {
        const isCollapsed = Boolean(collapsedMap[idx]);

        return (
          <div
              key={idx}
              className={`fb-card conditional-rule-card ${isCollapsed ? "conditional-rule-card-collapsed" : ""}`}
              style={{ padding: 12, marginBottom: 8 }}
            >
            <div className="conditional-rule-header">
              <div className="conditional-rule-header-left">
                <div className="conditional-rule-title">Rule {idx + 1}</div>
                <div className="conditional-rule-summary">
                  {isCollapsed ? (
                    <div className="conditional-summary-badges">
                      <span className="summary-pill summary-pill-neutral">
                        {fmtRange(r.minScore, r.maxScore)}
                      </span>

                      {(!r.allowedRecommendations || r.allowedRecommendations.length === 0) ? (
                        <span className="summary-pill summary-pill-neutral">Rec: all</span>
                      ) : (
                        r.allowedRecommendations.map((rec) => (
                          <span
                            key={rec}
                            className={`summary-pill ${getRecBadgeClass(rec)}`}
                          >
                            {rec}
                          </span>
                        ))
                      )}

                      {(!r.pulses || r.pulses.length === 0) ? (
                        <span className="summary-pill summary-pill-neutral">Pulse: any</span>
                      ) : (
                        r.pulses.map((pulse) => (
                          <span
                            key={pulse}
                            className={`summary-pill ${getPulseBadgeClass(pulse)}`}
                          >
                            {pulse}
                          </span>
                        ))
                      )}

                      {(!r.marketPulseRules || r.marketPulseRules.length === 0) ? (
                        <span className="summary-pill summary-pill-neutral">Rule: any</span>
                      ) : (
                        r.marketPulseRules.map((rule) => (
                          <span key={rule} className="summary-pill summary-pill-rule">
                            {rule}
                          </span>
                        ))
                      )}

                      <span className="summary-pill summary-pill-neutral">
                        {r.tpOverridePct != null ? `TP: ${r.tpOverridePct}%` : "TP: none"}
                      </span>
                    </div>
                  ) : (
                    "Configure score, recommendation, pulse, market pulse rule, and TP override."
                  )}
                </div>
              </div>

              <div className="conditional-rule-header-right">
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => toggleCollapsed(idx)}
                >
                  {isCollapsed ? "Expand" : "Collapse"}
                </button>
              </div>
            </div>

            {!isCollapsed && (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 1fr)",
                    gap: 12,
                    alignItems: "center"
                  }}
                >
                  <div className="fb-field">
                    <label>Min score (inclusive)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={r.minScore ?? ""}
                      onChange={e => update(idx, { minScore: e.target.value === "" ? undefined : Number(e.target.value) })}
                    />
                  </div>

                  <div className="fb-field">
                    <label>Max score (inclusive)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={r.maxScore ?? ""}
                      onChange={e => update(idx, { maxScore: e.target.value === "" ? undefined : Number(e.target.value) })}
                    />
                  </div>

                  <div className="fb-field" style={{ gridColumn: "span 2" }}>
                    <label>Allowed recommendations</label>
                    <div className="chip-row">
                      {ALL_REC_OPTIONS.map(o => {
                        const active = (r.allowedRecommendations || []).map(x => x.toUpperCase()).includes(o);
                        return (
                          <button
                            key={o}
                            type="button"
                            className={`chip ${active ? "active" : ""}`}
                            onClick={() => toggleRec(idx, o)}
                          >
                            {o}
                          </button>
                        );
                      })}
                    </div>
                    <div className="hint">Leave empty to allow all recs for this rule.</div>
                  </div>

                  <div className="fb-field">
                    <label>TP override % (optional)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={r.tpOverridePct ?? ""}
                      onChange={e =>
                        update(idx, {
                          tpOverridePct: e.target.value === "" ? undefined : Number(e.target.value)
                        })
                      }
                    />
                    <div className="hint">If set, this TP % overrides score-based TP rules for this matched rule.</div>
                  </div>
                </div>

                <div className="fb-field" style={{ marginTop: 8 }}>
                  <label>Market Pulse (optional)</label>
                  <div className="chip-row">
                    {PULSE_OPTIONS.map(p => {
                      const active = (r.pulses || []).map(x => x.toLowerCase()).includes(p);
                      return (
                        <button
                          key={p}
                          type="button"
                          className={`chip ${active ? "active" : ""}`}
                          onClick={() => togglePulse(idx, p)}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>
                  <div className="hint">
                    If none selected, the rule applies to <em>any</em> pulse. If you pick pulses, the rule only applies when the current pulse matches.
                  </div>
                </div>

                <div className="fb-field" style={{ marginTop: 8 }}>
                  <label>Market Pulse Rule (optional)</label>
                  <div className="chip-row">
                    {MARKET_PULSE_RULE_OPTIONS.map(rule => {
                      const active = (r.marketPulseRules || []).some(
                        x => String(x).toLowerCase() === rule.toLowerCase()
                      );

                      return (
                        <button
                          key={rule}
                          type="button"
                          className={`chip ${active ? "active" : ""}`}
                          onClick={() => toggleMarketPulseRule(idx, rule)}
                        >
                          {rule}
                        </button>
                      );
                    })}
                  </div>
                  <div className="hint">
                    If none selected, the rule applies to <em>any</em> market pulse rule. If you pick values here, the rule only applies when the trade&apos;s marketPulseRule matches.
                  </div>
                </div>
              </>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button type="button" className="btn ghost" onClick={() => move(idx, -1)} disabled={idx === 0}>
                Move up
              </button>
              <button type="button" className="btn ghost" onClick={() => move(idx, 1)} disabled={idx === (rules.length - 1)}>
                Move down
              </button>
              <div className="fb-spacer" />
              <button type="button" className="btn dangerous" onClick={() => removeRule(idx)}>
                Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}